import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useColumnVisibility, type CellValue, type GridFilter, type GridPage, type GridState } from '../lib/grid.ts';
import { Button } from './ui/Button.tsx';

/**
 * 공용 데이터테이블. 서버 모드(/grid 응답)와 클라이언트 모드(lib/grid.ts useClientGrid 결과) 모두 같은 props 를 받는다.
 *  - 헤더 클릭: 정렬 (asc → desc → 없음), 필터 아이콘: 팝오버 (텍스트 검색 / 체크박스 목록 + 선택만·제외 모드 + 초기화)
 *  - 페이지네이션: 이전/다음, 페이지 번호 입력, 페이지 크기 (pagination='auto' 면 한 페이지에 다 들어갈 때 숨김)
 *  - 짝수 행 배경. compact 면 검색창·건수 행을 숨긴다 (상세 화면의 부속 표). size='sm' 은 셀 여백·글자를 줄인다
 *  - CR-062: id 를 주면 도구 행 우측 「열」 버튼으로 열을 숨기고 localStorage(prism.grid.cols.{id}) 에 그리드별로 보존한다.
 *    첫 1단 열과 key 가 '_' 로 시작하는 행 작업 열, lock:true 열은 숨길 수 없다
 */
export interface Column<T> {
  key: string;
  label: string;
  width?: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  filter?: 'text' | 'select' | 'bool' | 'date' | 'none';
  /** 달력 날짜 컬럼(클라이언트 모드 필터): 시간대 변환 없이 날짜만 비교 */
  day?: boolean;
  /** select 형 선택지 (없으면 응답 options[key]) */
  options?: Array<{ value: string; label: string }>;
  render?: (row: T) => ReactNode;
  /** 클라이언트 모드 전용: 정렬 기준값 / 필터·검색 기준값 / select 선택지 라벨 */
  sortValue?: (row: T) => CellValue;
  filterValue?: (row: T) => CellValue;
  optionLabel?: (value: string) => string;
  /** 2단 행(CR-028): 2 면 기록의 둘째 줄에 놓인다. span 은 1단 열 몇 개 폭을 차지할지(기본 균등 배분, 마지막 열이 나머지). 머리글도 둘째 줄에 같은 폭으로 놓인다 */
  row?: 1 | 2;
  span?: number;
  /** CR-049: 2단 셀이 시작할 1단 열 인덱스(0부터). 생략하면 앞 2단 셀 다음 열. 덮이지 않은 1단 열은 두 줄을 합쳐(rowSpan) 세로 중앙정렬 */
  at?: number;
  /** 긴 텍스트: 줄바꿈 허용. 기본은 모든 셀 nowrap (1200px 에서 개행 없이 보이도록 폭·2단 행으로 맞춘다) */
  wrap?: boolean;
  /** CR-062: 숨길 수 없는 열. 첫 1단 열과 key 가 '_' 로 시작하는 열은 지정하지 않아도 잠긴다 */
  lock?: boolean;
}

export function DataTable<T extends object>({ id, columns: allColumns, data, state, onChange, rowKey, toolbar, emptyText = '없음', isFetching, compact = false, pagination = 'always', size = 'md', rowClassName }: {
  /** CR-062: 열 표시·숨김 설정을 저장할 그리드 식별자. 없으면 열 설정 버튼이 없다 */
  id?: string;
  columns: Column<T>[]; data?: GridPage<T>; state: GridState; onChange: (patch: Partial<GridState>) => void; rowKey?: (r: T, i: number) => string;
  toolbar?: ReactNode; emptyText?: string; isFetching?: boolean;
  /** 검색창·건수 행 숨김 (필터가 걸려 있으면 해제 버튼만 보인다) */
  compact?: boolean;
  /** always: 항상 표시 · auto: 행 수가 페이지 크기를 넘을 때만 · none: 숨김 (pageSize 0 과 함께 사용) */
  pagination?: 'always' | 'auto' | 'none';
  size?: 'md' | 'sm';
  /** 행 강조 등 추가 클래스. 'dt-hl' 은 강조 배경 */
  rowClassName?: (row: T) => string;
}) {
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  // CR-062: 열 숨김. 잠긴 열(첫 1단 열·'_' 작업 열·lock)은 항상 보인다
  const firstPrimaryKey = allColumns.find((c) => c.row !== 2)?.key;
  const isLocked = (c: Column<T>) => !!c.lock || c.key === firstPrimaryKey || c.key.startsWith('_');
  const keys = useMemo(() => allColumns.map((c) => c.key), [allColumns]); // eslint-disable-line react-hooks/exhaustive-deps
  const vis = useColumnVisibility(id, keys);
  const [openCols, setOpenCols] = useState(false);
  const columns = useMemo(() => allColumns.filter((c) => isLocked(c) || !vis.hidden.includes(c.key)), [allColumns, vis.hidden]); // eslint-disable-line react-hooks/exhaustive-deps
  // 2단 열의 at 은 원래 1단 열 인덱스 기준이므로 숨긴 열을 뺀 인덱스로 옮긴다
  const remapAt = (at: number | undefined) => { if (at === undefined) return undefined; const orig = allColumns.filter((c) => c.row !== 2); let n = 0; for (let i = 0; i < at && i < orig.length; i++) if (columns.includes(orig[i])) n++; return n; };
  const filterOf = (key: string) => state.filters.filter((f) => f.col === key);
  const setFilter = (key: string, fs: GridFilter[]) => onChange({ filters: [...state.filters.filter((x) => x.col !== key), ...fs] });
  const toggleSort = (key: string) => {
    if (state.sort?.col !== key) return onChange({ sort: { col: key, dir: 'asc' } });
    if (state.sort.dir === 'asc') return onChange({ sort: { col: key, dir: 'desc' } });
    onChange({ sort: null });
  };
  const total = data?.total ?? 0;
  const pages = state.pageSize > 0 ? Math.max(1, Math.ceil(total / state.pageSize)) : 1;
  const showPager = pagination === 'always' || (pagination === 'auto' && state.pageSize > 0 && total > state.pageSize);
  const showToolbarRow = !compact || state.filters.length > 0 || !!toolbar || !!id;
  const [pageInput, setPageInput] = useState(String(state.page));
  useEffect(() => setPageInput(String(state.page)), [state.page]);
  const go = (p: number) => onChange({ page: Math.min(pages, Math.max(1, p)) });
  // CR-028: 1단(기본) 열과 2단 열. 2단 열은 colSpan 으로 1단 열 폭을 나눠 쓴다
  const primary = columns.filter((c) => c.row !== 2); const secondary = columns.filter((c) => c.row === 2);
  // CR-049: 2단 열은 앞에서부터 span(기본 1)만큼 1단 열을 덮는다. 덮이지 않은 뒤쪽 1단 열은 두 줄을 합쳐(rowSpan) 세로 중앙정렬 → 열 구분선이 끊기지 않는다
  const layout = useMemo(() => {
    let cursor = 0; const covered = new Set<number>();
    const cells = secondary.map((c) => { const at = remapAt(c.at); const start = Math.min(Math.max(cursor, at ?? cursor), Math.max(0, primary.length - 1)); const span = Math.max(1, Math.min(c.span ?? 1, primary.length - start)); cursor = start + span; for (let i = start; i < start + span; i++) covered.add(i); return { col: c, start, span }; });
    return { cells, covered };
  }, [columns]); // eslint-disable-line react-hooks/exhaustive-deps
  const merged = (i: number) => secondary.length > 0 && !layout.covered.has(i); // 이 1단 열은 2단 셀이 없어 두 줄을 합친다
  const startsAt = (i: number) => layout.cells.find((x) => x.start === i);
  const tdClass = (c: Column<T>, rs = false) => `border-b border-slate-200 ${rs ? 'align-middle dt-rs' : 'align-top'} ${c.align === 'right' ? 'text-right tabular-nums' : c.align === 'center' ? 'text-center' : ''}${c.wrap ? ' dt-wrap' : ''}`;
  const cell = (c: Column<T>, r: T) => (c.render ? c.render(r) : String((r as Record<string, unknown>)[c.key] ?? ''));
  const th = (c: Column<T>, colSpan?: number, rowSpan?: number, extra = '') => {
    const sorted = state.sort?.col === c.key ? state.sort.dir : null;
    const fs = filterOf(c.key); const f = fs[0];
    const filterable = c.filter && c.filter !== 'none';
    return (
      <th key={c.key} colSpan={colSpan} rowSpan={rowSpan} style={{ width: colSpan ? undefined : c.width }} className={`relative bg-slate-50 border-b border-slate-200 text-left font-semibold text-slate-700 select-none ${c.align === 'right' ? 'text-right' : ''}${c.wrap ? ' dt-wrap' : ''} ${extra}`}>
        <div className="flex items-center gap-1">
          <button type="button" className={`flex-1 text-left ${c.sortable === false ? 'cursor-default' : 'hover:text-accent-strong'}`} onClick={() => c.sortable !== false && toggleSort(c.key)}>
            {c.label}{c.sortable !== false && <span className={`ml-1 text-[10px] ${sorted ? 'text-accent-strong' : 'text-slate-400'}`}>{sorted === 'asc' ? '▲' : sorted === 'desc' ? '▼' : '◇'}</span>}
          </button>
          {filterable && <button type="button" title="필터" className={`text-[11px] px-1 ${f ? 'bg-accent text-white' : 'text-slate-400 hover:text-accent-strong'}`} onClick={() => setOpenFilter(openFilter === c.key ? null : c.key)}>☰</button>}
        </div>
        {openFilter === c.key && <FilterPopover column={c} filters={fs} options={c.options ?? data?.options?.[c.key] ?? []} onApply={(nf) => { setFilter(c.key, nf); setOpenFilter(null); }} onClose={() => setOpenFilter(null)} />}
      </th>
    );
  };

  return (
    <div className="dt">
      {showToolbarRow && <div className="flex flex-wrap items-center gap-2 mb-2">
        {!compact && <input className="h-7 w-56 border border-slate-300 px-2 text-[12.5px]" placeholder="검색 (Enter)" defaultValue={state.q} key={state.q} onKeyDown={(e) => { if (e.key === 'Enter') onChange({ q: (e.target as HTMLInputElement).value.trim() }); }} />}
        {state.filters.length > 0 && <Button variant="ghost" onClick={() => onChange({ filters: [], q: '' })}>필터 모두 해제 ({state.filters.length})</Button>}
        {!compact && <span className="text-[12px] text-slate-500">검색결과 <b className="text-slate-800">{total.toLocaleString()}</b>건{isFetching && ' · 갱신 중…'}</span>}
        <span className="flex-1" />
        {toolbar}
        {id && <span className="relative">
          <button type="button" aria-expanded={openCols} title="표시할 열 고르기" onClick={() => setOpenCols(!openCols)}
            className={`inline-flex h-7 items-center gap-1 px-1 text-[12px] hover:underline focus:outline-none focus:underline ${openCols || vis.hidden.length > 0 ? 'text-accent-strong' : 'text-slate-500 hover:text-slate-800'}`}>
            <ColumnsIcon />
            <span>열 설정</span>
            {vis.hidden.length > 0 && <span className="tabular-nums">({allColumns.length - vis.hidden.length}/{allColumns.length})</span>}
          </button>
          {openCols && <ColumnsPopover columns={allColumns} hidden={vis.hidden} locked={isLocked} sortCol={state.sort?.col ?? null} filterCols={state.filters.map((f) => f.col)} onToggle={vis.toggle} onReset={() => { vis.reset(); }} onClose={() => setOpenCols(false)} />}
        </span>}
      </div>}
      <div className="overflow-x-auto border border-slate-200">
        <table className={`w-full border-collapse dt-table${size === 'sm' ? ' dt-sm' : ''}`}>
          <thead>
            <tr>{primary.map((c, i) => th(c, undefined, merged(i) ? 2 : undefined))}</tr>
            {secondary.length > 0 && <tr className="dt-h2">{primary.map((_, i) => { const x = startsAt(i); return x ? th(x.col, x.span, undefined, x.start > 0 ? 'dt-bl' : '') : null; })}</tr>}
          </thead>
          {/* 기록 하나 = tbody 하나 (1단 행 + 선택적 2단 행). 짝수 배경·hover 는 tbody 단위 */}
          {data?.rows.map((r, i) => (
            <tbody key={rowKey ? rowKey(r, i) : String((r as { id?: string }).id ?? i)} className={`dt-rec${rowClassName ? ` ${rowClassName(r)}` : ''}`}>
              <tr className="dt-r1">{primary.map((c, i) => <td key={c.key} rowSpan={merged(i) ? 2 : undefined} className={tdClass(c, merged(i))}>{cell(c, r)}</td>)}</tr>
              {secondary.length > 0 && <tr className="dt-r2">{primary.map((_, i) => { const x = startsAt(i); return x ? <td key={x.col.key} colSpan={x.span} className={`${tdClass(x.col)}${x.start > 0 ? ' dt-bl' : ''}`}>{cell(x.col, r)}</td> : null; })}</tr>}
            </tbody>
          ))}
          {data && data.rows.length === 0 && <tbody><tr><td colSpan={primary.length} className="py-8 text-center text-slate-500">{emptyText}</td></tr></tbody>}
        </table>
      </div>
      {showPager && <div className="flex flex-wrap items-center gap-2 mt-2 text-[12px] text-slate-600">
        <Button variant="secondary" disabled={state.page <= 1} onClick={() => go(1)}>«</Button>
        <Button variant="secondary" disabled={state.page <= 1} onClick={() => go(state.page - 1)}>‹ 이전</Button>
        <span>페이지</span>
        <input className="h-6 w-12 border border-slate-300 px-1 text-center" value={pageInput} onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ''))} onKeyDown={(e) => { if (e.key === 'Enter') go(Number(pageInput) || 1); }} onBlur={() => { if (pageInput && Number(pageInput) !== state.page) go(Number(pageInput)); }} />
        <span>/ {pages}</span>
        <Button variant="secondary" disabled={state.page >= pages} onClick={() => go(state.page + 1)}>다음 ›</Button>
        <Button variant="secondary" disabled={state.page >= pages} onClick={() => go(pages)}>»</Button>
        <span className="ml-3">행 수</span>
        <select className="h-6 border border-slate-300 px-1" value={state.pageSize} onChange={(e) => onChange({ pageSize: Number(e.target.value), page: 1 })}>{[...new Set([state.pageSize, 20, 50, 100, 200])].filter((n) => n > 0).sort((a, b) => a - b).map((n) => <option key={n} value={n}>{n}</option>)}</select>
        <span className="text-slate-400">{total ? `${(state.page - 1) * state.pageSize + 1}–${Math.min(total, state.page * state.pageSize)} / ${total.toLocaleString()}` : ''}</span>
      </div>}
    </div>
  );
}

/** CR-062: 열 표시·숨김 버튼 아이콘 (세 개의 세로 열) */
function ColumnsIcon() {
  return <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true" className="shrink-0"><rect x="1" y="2" width="3" height="10" fill="currentColor" /><rect x="5.5" y="2" width="3" height="10" fill="currentColor" opacity=".6" /><rect x="10" y="2" width="3" height="10" fill="currentColor" opacity=".35" /></svg>;
}

/** CR-062: 열 표시·숨김 팝오버. 머리(표시 수·모두 표시) + 열 목록(체크 행, 잠긴 열은 「고정」). 바깥 클릭·Esc 로 닫힌다 */
function ColumnsPopover<T>({ columns, hidden, locked, sortCol, filterCols, onToggle, onReset, onClose }: { columns: Column<T>[]; hidden: string[]; locked: (c: Column<T>) => boolean; sortCol: string | null; filterCols: string[]; onToggle: (key: string) => void; onReset: () => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', h); document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [onClose]);
  const shown = columns.length - hidden.length;
  const name = (c: Column<T>) => c.label || (c.key.startsWith('_') ? '행 작업' : c.key);
  return (
    <div ref={ref} role="dialog" aria-label="표시할 열" className="absolute right-0 top-full z-20 mt-1 w-60 border border-slate-300 bg-white shadow-lg text-[12px] font-normal text-slate-800">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-2.5 py-1.5">
        <span className="font-semibold text-slate-700">표시할 열 <span className="ml-1 font-normal text-slate-500 tabular-nums">{shown}/{columns.length}</span></span>
        <button type="button" className={`text-[11px] ${hidden.length > 0 ? 'text-accent-strong hover:underline' : 'text-slate-400 cursor-default'}`} disabled={hidden.length === 0} onClick={onReset}>모두 표시</button>
      </div>
      <ul className="max-h-72 overflow-y-auto py-1">
        {columns.map((c) => { const lk = locked(c); const on = lk || !hidden.includes(c.key); const used = sortCol === c.key || filterCols.includes(c.key); return (
          <li key={c.key}>
            <label className={`flex h-7 items-center gap-2 px-2.5 ${lk ? 'text-slate-400' : 'cursor-pointer hover:bg-slate-50'}`}>
              <input type="checkbox" className="accent-accent" checked={on} disabled={lk} onChange={() => onToggle(c.key)} />
              <span className="flex-1 truncate">{name(c)}</span>
              {lk && <span className="text-[10px] text-slate-400">고정</span>}
              {!lk && used && <span className="text-[10px] text-accent-strong" title="정렬·필터가 걸린 열">{sortCol === c.key ? '정렬' : '필터'}</span>}
            </label>
          </li>); })}
      </ul>
    </div>
  );
}

function FilterPopover<T>({ column, filters, options, onApply, onClose }: { column: Column<T>; filters: GridFilter[]; options: Array<{ value: string; label: string }>; onApply: (f: GridFilter[]) => void; onClose: () => void }) {
  const filter = filters.find((f) => f.op !== 'lte') ?? filters[0];
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); }; document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, [onClose]);
  const kind = column.filter === 'bool' ? 'select' : column.filter;
  const opts = column.filter === 'bool' && options.length === 0 ? [{ value: 'true', label: '예' }, { value: 'false', label: '아니오' }] : options;
  const [text, setText] = useState(filter?.op === 'contains' ? filter.value[0] : '');
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<'in' | 'nin'>(filter?.op === 'nin' ? 'nin' : 'in');
  const [sel, setSel] = useState<string[]>(filter && (filter.op === 'in' || filter.op === 'nin') ? filter.value : filter?.op === 'eq' || filter?.op === 'bool' ? filter.value : []);
  const [from, setFrom] = useState(filters.find((f) => f.op === 'gte')?.value[0] ?? ''); const [to, setTo] = useState(filters.find((f) => f.op === 'lte')?.value[0] ?? '');
  const visible = useMemo(() => opts.filter((o) => !search || o.label.toLowerCase().includes(search.toLowerCase())), [opts, search]);
  return (
    <div ref={ref} className="absolute left-0 top-full z-20 mt-1 w-64 border border-slate-300 bg-white shadow-lg p-2 text-[12px] font-normal text-slate-800">
      {kind === 'text' && (
        <form onSubmit={(e) => { e.preventDefault(); onApply(text.trim() ? [{ col: column.key, op: 'contains', value: [text.trim()] }] : []); }}>
          <input autoFocus className="w-full h-7 border border-slate-300 px-2" placeholder="포함하는 문자" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex gap-1 mt-2"><Button type="submit">적용</Button><Button variant="secondary" onClick={() => onApply([])}>필터 초기화</Button></div>
        </form>
      )}
      {kind === 'select' && (
        <div>
          {opts.length > 6 && <input autoFocus className="w-full h-7 border border-slate-300 px-2 mb-1" placeholder="검색" value={search} onChange={(e) => setSearch(e.target.value)} />}
          <div className="flex gap-1 mb-1 text-[11px]">
            <button type="button" className={`px-1.5 py-0.5 border ${mode === 'in' ? 'bg-accent text-white border-accent' : 'border-slate-300'}`} onClick={() => setMode('in')}>선택 항목만 보기</button>
            <button type="button" className={`px-1.5 py-0.5 border ${mode === 'nin' ? 'bg-accent text-white border-accent' : 'border-slate-300'}`} onClick={() => setMode('nin')}>선택 항목 제외</button>
          </div>
          <div className="max-h-52 overflow-auto border border-slate-200">
            {visible.map((o) => (
              <label key={o.value} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={sel.includes(o.value)} onChange={(e) => setSel(e.target.checked ? [...sel, o.value] : sel.filter((v) => v !== o.value))} />{o.label}
              </label>
            ))}
            {visible.length === 0 && <div className="px-2 py-2 text-slate-400">항목 없음</div>}
          </div>
          <div className="flex gap-1 mt-2">
            <Button onClick={() => onApply(sel.length ? [{ col: column.key, op: column.filter === 'bool' ? (sel.length === 1 ? 'bool' : 'in') : mode, value: sel }] : [])}>적용</Button>
            <Button variant="secondary" onClick={() => { setSel([]); onApply([]); }}>필터 초기화</Button>
            <span className="flex-1" /><span className="text-slate-400 self-center">{sel.length}개 선택</span>
          </div>
        </div>
      )}
      {kind === 'date' && (
        <div>
          <label className="block mb-1">시작 <input type="date" className="w-full h-7 border border-slate-300 px-2" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="block mb-2">끝 <input type="date" className="w-full h-7 border border-slate-300 px-2" value={to} onChange={(e) => setTo(e.target.value)} /></label>
          <div className="flex gap-1">
            <Button onClick={() => onApply([...(from ? [{ col: column.key, op: 'gte' as const, value: [from] }] : []), ...(to ? [{ col: column.key, op: 'lte' as const, value: [to] }] : [])])}>적용</Button>
            <Button variant="secondary" onClick={() => onApply([])}>필터 초기화</Button>
          </div>
        </div>
      )}
    </div>
  );
}
